/**
 * S-56 台紙作成 一覧・作成（F-EXP-05）。kintone: 台紙出力（一覧画面）
 */
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { FilePlus2, FileStack, Search } from 'lucide-react';
import { Button, DataTable, DateField, EmptyState, ErrorState, FormField, FormGrid, PageHeader, Panel, useToast, type Column } from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { useRequestReportJob } from '../common/api';
import { Money, OperatorSelect, VesselExcludePicker } from '../common/components';
import { useOperatorScope } from '../common/useOperatorScope';
import { useGenerateMounts, useMounts, type MountListItem } from './api';

type FormValues = { businessOperatorId: number | null; from: string; to: string; excludeVesselIds: number[] };

export function MountListPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { year, yearPath } = useFiscalYear();
  const { isCoop, fixedOperatorId } = useOperatorScope();
  const [params, setParams] = useSearchParams();
  const criteria = useMemo<FormValues>(
    () => ({
      businessOperatorId: params.get('businessOperatorId') ? Number(params.get('businessOperatorId')) : null,
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      excludeVesselIds: params.getAll('excludeVesselIds').map(Number).filter(Boolean),
    }),
    [params],
  );
  const setCriteria = (x: FormValues) => {
    const next = new URLSearchParams();
    if (x.businessOperatorId != null) next.set('businessOperatorId', String(x.businessOperatorId));
    if (x.from) next.set('from', x.from);
    if (x.to) next.set('to', x.to);
    x.excludeVesselIds.forEach((id) => next.append('excludeVesselIds', String(id)));
    setParams(next, { replace: true });
  };
  const operatorId = isCoop ? fixedOperatorId : criteria.businessOperatorId;
  const list = useMounts(year, { businessOperatorId: operatorId, from: criteria.from, to: criteria.to, excludeVesselIds: criteria.excludeVesselIds });
  const generate = useGenerateMounts();
  const job = useRequestReportJob();
  const { control, handleSubmit, watch, register } = useForm<FormValues>({ values: { ...criteria, businessOperatorId: operatorId } });

  const onCreate = handleSubmit(async (v) => {
    if (!isCoop && v.businessOperatorId == null) return toast.error('事業実施者IDは必須です');
    if (!v.from || !v.to) return toast.error('調査日は必須です');
    try {
      const res = await generate.mutateAsync({ fiscalYear: year, businessOperatorId: isCoop ? null : v.businessOperatorId, from: v.from, to: v.to, excludeVesselIds: v.excludeVesselIds });
      toast.success('帳票作成が完了しました。', `${res.ids.length} 件の台紙を作成しました`);
      setCriteria(v);
    } catch (e) {
      toast.error('台紙を作成できませんでした', errorMessage(e));
    }
  });

  const onBulk = async () => {
    try {
      await job.mutateAsync({
        reportId: 'R-11',
        fiscalYear: year,
        params: { fiscalYear: year, businessOperatorId: operatorId ?? undefined, from: criteria.from || undefined, to: criteria.to || undefined, excludeVesselIds: criteria.excludeVesselIds },
      });
      toast.success('帳票一括作成を依頼しました', '「帳票一括作成ジョブ」画面で作成状況を確認し、ZIP をダウンロードしてください。');
      navigate(yearPath('report-jobs'));
    } catch (e) {
      toast.error('一括出力を依頼できませんでした', errorMessage(e));
    }
  };

  const columns: Column<MountListItem>[] = [
    { key: 'rosterNo', header: '名簿No', numeric: true, width: 84, sortable: true },
    { key: 'vesselName', header: '船名', sortable: true, width: 170, nowrap: true },
    ...(!isCoop ? [{ key: 'businessOperatorName', header: '事業実施者', width: 170 } satisfies Column<MountListItem>] : []),
    { key: 'period', header: '調査期間（出港〜帰港）', width: 205, nowrap: true, render: (r) => <span className="bs-num">{r.periodFrom}〜{r.periodTo}</span> },
    { key: 'invoiceDate', header: '請求日', width: 110, sortable: true, render: (r) => <span className="bs-num">{r.invoiceDate ?? '—'}</span> },
    { key: 'fuelPayee', header: '燃油代 支払先', width: 125, nowrap: true },
    { key: 'fuelTotal', header: '燃油代', width: 110, align: 'right', render: (r) => <Money value={r.fuelTotal} /> },
    { key: 'lubeTotal', header: '潤滑油代', width: 100, align: 'right', render: (r) => <Money value={r.lubeTotal} /> },
    { key: 'devTotal', header: '現像代', width: 100, align: 'right', render: (r) => <Money value={r.devTotal} /> },
  ];

  return (
    <>
      <PageHeader eyebrow="各種様式" title="台紙作成" description="速報（1出港）ごとに燃油代・潤滑油代・現像代の領収書台紙を作成し、整理番号・支払先を入力して出力します。" />
      <Panel title="台紙作成・検索条件">
        <form
          className="bs-exp-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit((v) => setCriteria(v))();
          }}
        >
          <FormGrid columns={3}>
            <FormField label="事業実施者" required htmlFor="mount-operator">
              <Controller control={control} name="businessOperatorId" render={({ field }) => <OperatorSelect id="mount-operator" year={year} value={field.value} onChange={field.onChange} />} />
            </FormField>
            <FormField label="調査日" required span={2} hint="調査期間が範囲と重なる速報ごとに 1 件作成します">
              <span className="bs-exp-range">
                <DateField {...register('from')} aria-label="調査日FROM" />
                <span>〜</span>
                <DateField {...register('to')} aria-label="調査日TO" />
              </span>
            </FormField>
          </FormGrid>
          <Controller
            control={control}
            name="excludeVesselIds"
            render={({ field }) => <VesselExcludePicker year={year} operatorId={isCoop ? fixedOperatorId : watch('businessOperatorId')} value={field.value} onChange={field.onChange} />}
          />
          <div className="bs-exp-row bs-exp-row--end">
            <Button type="submit" icon={<Search size={16} />}>
              検索
            </Button>
            <Button variant="primary" icon={<FilePlus2 size={16} />} onClick={() => void onCreate()} loading={generate.isPending}>
              帳票作成
            </Button>
          </div>
        </form>
      </Panel>
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <DataTable
          caption="台紙一覧"
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={list.isPending}
          pagination={{ pageSize: 50 }}
          onRowClick={(r) => navigate(yearPath(`forms/receipt-mounts/${r.id}`))}
          toolbar={
            <Button size="sm" icon={<FileStack size={15} />} onClick={() => void onBulk()} loading={job.isPending} disabled={!list.data?.items.length}>
              一括出力（燃油代 ZIP）
            </Button>
          }
          empty={<EmptyState compact icon={<FileStack />} title="台紙はありません" description="条件を指定して「帳票作成」を押してください。" />}
        />
      )}
    </>
  );
}
